import type { VolunteerOrgProject, VolunteerOrganization } from "@entities";
import { volunteerOrgProjectAssignmentRepo } from "@repositories";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AssignVolunteerOrgMissionsDialog } from "./AssignVolunteerOrgMissionsDialog";
import { VolunteerOrgLocationField } from "./VolunteerOrgSharedFields";
import { FormSection, quietButtonSx, surfaceCardSx } from "./volunteer-org-form-ui";

const emptyAssignment = () => ({
  projectId: "",
  volunteerCount: "",
  address: "",
  coordinates: null as { lat: number; lng: number } | null,
  notes: "",
});

export function VolunteerOrgAssignmentsPanel({
  organization,
  projects,
}: {
  organization: VolunteerOrganization;
  projects: VolunteerOrgProject[];
}) {
  const queryClient = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignment, setAssignment] = useState(emptyAssignment);
  const [error, setError] = useState<string | null>(null);
  const activeProjects = projects.filter((project) => project.isActive);

  const { data: assignments = [] } = useQuery({
    queryKey: ["volunteer-org-unit-assignments", organization.id],
    queryFn: () =>
      volunteerOrgProjectAssignmentRepo.find({
        where: { volunteerOrganizationId: organization.id },
        include: { project: true },
        orderBy: { createdAt: "desc" },
      }),
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["volunteer-org-unit-assignments", organization.id],
      }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-assignments"] }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-branches"] }),
    ]);
  };

  const addAssignment = async () => {
    setError(null);
    try {
      await volunteerOrgProjectAssignmentRepo.insert({
        volunteerOrganizationId: organization.id,
        projectId: assignment.projectId,
        volunteerCount: (() => {
          const value = Number(assignment.volunteerCount);
          if (!Number.isFinite(value) || value < 0) {
            throw new Error("מספר המתנדבים חייב להיות אפס או יותר");
          }
          return Math.floor(value);
        })(),
        address: assignment.address,
        coordinates: assignment.coordinates,
        notes: assignment.notes,
      });
      setAssignment(emptyAssignment());
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "שמירת שיבוץ נכשלה");
    }
  };

  return (
    <Stack gap={2}>
      {assignments.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
          אין שיבוצים עדיין.
        </Typography>
      ) : (
        assignments.map((row) => (
          <Stack
            key={row.id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={surfaceCardSx}
          >
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {row.project?.name ?? "פרויקט"}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                {row.volunteerCount} מתנדבים · {row.address}
              </Typography>
            </Stack>
            <IconButton
              aria-label="מחיקת שיבוץ"
              onClick={async () => {
                await volunteerOrgProjectAssignmentRepo.delete(row.id);
                await refresh();
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))
      )}
      <Button
        variant="outlined"
        sx={{ ...quietButtonSx, alignSelf: "flex-start" }}
        onClick={() => setAssignOpen(true)}
      >
        שיוך למשימות
      </Button>
      <FormSection title="שיבוץ חדש">
        <Stack gap={1.5}>
          <TextField
            select
            size="small"
            label="פרויקט"
            value={assignment.projectId}
            onChange={(event) =>
              setAssignment({ ...assignment, projectId: event.target.value })
            }
          >
            {activeProjects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="מספר מתנדבים"
            type="number"
            value={assignment.volunteerCount}
            onChange={(event) =>
              setAssignment({ ...assignment, volunteerCount: event.target.value })
            }
          />
          <VolunteerOrgLocationField
            required
            label="מיקום ספציפי"
            value={{
              address: assignment.address,
              coordinates: assignment.coordinates,
            }}
            onChange={(next) => setAssignment({ ...assignment, ...next })}
          />
          <TextField
            size="small"
            label="הערות"
            value={assignment.notes}
            onChange={(event) =>
              setAssignment({ ...assignment, notes: event.target.value })
            }
          />
          {error ? (
            <Typography color="error" sx={{ fontSize: 13 }}>
              {error}
            </Typography>
          ) : null}
          <Button
            variant="outlined"
            disabled={!assignment.projectId || !assignment.address.trim()}
            sx={{ ...quietButtonSx, alignSelf: "flex-start" }}
            onClick={() => void addAssignment()}
          >
            הוסף שיבוץ
          </Button>
        </Stack>
      </FormSection>
      <AssignVolunteerOrgMissionsDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSaved={() => void refresh()}
        volunteerOrganization={organization}
      />
    </Stack>
  );
}
